import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../utils/api';
import { DollarSign, Plus, CheckCircle, Clock, AlertCircle, X, Trash2, Pencil } from 'lucide-react';
import { motion } from 'framer-motion';

const PAYMENT_METHODS = [
  { id: 'bank_transfer', label: 'Bank Transfer' },
  { id: 'wise', label: 'Wise' },
  { id: 'paypal', label: 'PayPal' },
  { id: 'stripe', label: 'Stripe' },
  { id: 'payoneer', label: 'Payoneer' },
  { id: 'wire', label: 'Wire Transfer' },
  { id: 'crypto', label: 'Crypto' },
  { id: 'cheque', label: 'Cheque' },
  { id: 'other', label: 'Other' },
];

const COMMISSION_TYPES = ['sale', 'recurring', 'lead', 'bonus', 'adjustment'];
const COMMISSION_STATUSES = ['pending', 'confirmed', 'paid', 'cancelled', 'refunded'];

const labelize = (value = '') =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const methodLabel = (value = '') => PAYMENT_METHODS.find((item) => item.id === value)?.label || labelize(value);

const formatMoney = (value = 0, currency = 'USD') => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString().slice(0, 10);
};

const toIsoDate = (value) => {
  if (!value) return null;
  return `${value}T00:00:00+00:00`;
};

const CommissionsManager = () => {
  const [commissions, setCommissions] = useState([]);
  const [tools, setTools] = useState([]);
  const [stats, setStats] = useState({ summary: {}, by_status: [], by_tool: [], by_type: [], by_payment_method: [], trend: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCommission, setEditingCommission] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');
  const [filterToolId, setFilterToolId] = useState('all');
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const listParams = {};
      if (filterStatus !== 'all') listParams.status = filterStatus;
      if (filterType !== 'all') listParams.ctype = filterType;
      if (filterPaymentMethod !== 'all') listParams.payment_method = filterPaymentMethod;
      if (filterToolId !== 'all') listParams.tool_id = filterToolId;
      if (startDate) listParams.start_date = toIsoDate(startDate);
      if (endDate) listParams.end_date = `${endDate}T23:59:59+00:00`;

      const statsParams = { period };
      if (startDate) statsParams.start_date = toIsoDate(startDate);
      if (endDate) statsParams.end_date = `${endDate}T23:59:59+00:00`;

      const [commRes, toolsRes] = await Promise.all([
        adminApi.getCommissions(listParams),
        adminApi.getTools(),
      ]);
      const statsRes = await adminApi.getCommissionStats(statsParams).catch(() => ({
        data: { summary: {}, by_status: [], by_tool: [], by_type: [], by_payment_method: [], trend: [] },
      }));

      setCommissions(Array.isArray(commRes.data) ? commRes.data : []);
      setTools(Array.isArray(toolsRes.data) ? toolsRes.data : []);
      setStats(statsRes.data || { summary: {}, by_status: [], by_tool: [], by_type: [], by_payment_method: [], trend: [] });
    } catch (error) {
      console.error('Failed to load commissions:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, filterPaymentMethod, filterToolId, period, startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const applyStatusMutation = async (id, newStatus) => {
    setSaving(true);
    try {
      await adminApi.updateCommissionStatus(id, newStatus);
      await loadData();
    } catch {
      alert('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this commission record?')) return;
    setSaving(true);
    try {
      await adminApi.deleteCommission(id);
      await loadData();
    } catch {
      alert('Failed to delete commission');
    } finally {
      setSaving(false);
    }
  };

  const totals = {
    total: Number(stats?.summary?.total_amount || 0),
    pending: Number(stats?.summary?.pending_amount || 0),
    paid: Number(stats?.summary?.paid_amount || 0),
    recoveryRate: Number(stats?.summary?.recovery_rate || 0),
  };

  const statusIcon = (status) => {
    if (status === 'paid') return <CheckCircle size={14} className="text-laser-green" />;
    if (status === 'confirmed') return <AlertCircle size={14} className="text-blue-400" />;
    if (status === 'cancelled' || status === 'refunded') return <X size={14} className="text-red-400" />;
    return <Clock size={14} className="text-yellow-400" />;
  };

  const statusClass = (status) => {
    if (status === 'paid') return 'bg-laser-green/20 text-laser-green';
    if (status === 'confirmed') return 'bg-blue-400/20 text-blue-400';
    if (status === 'cancelled' || status === 'refunded') return 'bg-red-400/20 text-red-300';
    return 'bg-yellow-400/20 text-yellow-400';
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyber-yellow"></div></div>;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="commissions-manager">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Commissions</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="add-commission-button"
          >
            <Plus size={20} /> Log Commission
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="terminal-grid p-5">
          <div className="text-xs text-text-muted uppercase font-mono mb-1">Total</div>
          <div className="text-2xl font-black text-cyber-yellow">{formatMoney(totals.total)}</div>
        </div>
        <div className="terminal-grid p-5">
          <div className="text-xs text-text-muted uppercase font-mono mb-1">Pending</div>
          <div className="text-2xl font-black text-yellow-400">{formatMoney(totals.pending)}</div>
        </div>
        <div className="terminal-grid p-5">
          <div className="text-xs text-text-muted uppercase font-mono mb-1">Paid</div>
          <div className="text-2xl font-black text-laser-green">{formatMoney(totals.paid)}</div>
        </div>
        <div className="terminal-grid p-5">
          <div className="text-xs text-text-muted uppercase font-mono mb-1">Recovery Rate</div>
          <div className="text-2xl font-black text-blue-400">{totals.recoveryRate.toFixed(1)}%</div>
        </div>
      </div>

      <div className="terminal-grid p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-7 gap-3">
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="px-3 py-2 bg-surface border border-white/20 text-white text-sm">
            <option value="day">Trend: Day</option>
            <option value="week">Trend: Week</option>
            <option value="month">Trend: Month</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 bg-surface border border-white/20 text-white text-sm">
            <option value="all">All Status</option>
            {COMMISSION_STATUSES.map((status) => (
              <option key={status} value={status}>{labelize(status)}</option>
            ))}
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 bg-surface border border-white/20 text-white text-sm">
            <option value="all">All Types</option>
            {COMMISSION_TYPES.map((ctype) => (
              <option key={ctype} value={ctype}>{labelize(ctype)}</option>
            ))}
          </select>
          <select value={filterPaymentMethod} onChange={(e) => setFilterPaymentMethod(e.target.value)} className="px-3 py-2 bg-surface border border-white/20 text-white text-sm">
            <option value="all">All Payment Methods</option>
            {PAYMENT_METHODS.map((method) => (
              <option key={method.id} value={method.id}>{method.label}</option>
            ))}
          </select>
          <select value={filterToolId} onChange={(e) => setFilterToolId(e.target.value)} className="px-3 py-2 bg-surface border border-white/20 text-white text-sm">
            <option value="all">All Tools</option>
            {tools.map((tool) => (
              <option key={tool.id} value={tool.id}>{tool.name}</option>
            ))}
          </select>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-3 py-2 bg-transparent border border-white/20 text-white text-sm" />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="px-3 py-2 bg-transparent border border-white/20 text-white text-sm" />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <div className="terminal-grid p-4">
          <p className="text-xs text-text-muted uppercase font-mono mb-3">By Payment Method</p>
          <div className="space-y-2">
            {(stats.by_payment_method || []).slice(0, 6).map((row) => (
              <div key={row._id || 'unknown'} className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">{methodLabel(row._id || 'other')}</span>
                <span className="text-white font-semibold">{formatMoney(row.total || 0)}</span>
              </div>
            ))}
            {!stats.by_payment_method?.length && <p className="text-sm text-text-muted">No data</p>}
          </div>
        </div>

        <div className="terminal-grid p-4">
          <p className="text-xs text-text-muted uppercase font-mono mb-3">Top Tools</p>
          <div className="space-y-2">
            {(stats.by_tool || []).slice(0, 6).map((row) => (
              <div key={row._id || 'unknown'} className="flex items-center justify-between text-sm">
                <span className="text-text-secondary truncate max-w-[70%]">{row.tool_name || row._id || 'Unknown'}</span>
                <span className="text-white font-semibold">{formatMoney(row.total || 0)}</span>
              </div>
            ))}
            {!stats.by_tool?.length && <p className="text-sm text-text-muted">No data</p>}
          </div>
        </div>

        <div className="terminal-grid p-4">
          <p className="text-xs text-text-muted uppercase font-mono mb-3">Trend ({labelize(period)})</p>
          <div className="space-y-2">
            {(stats.trend || []).slice(-6).map((row) => (
              <div key={row._id || 'n/a'} className="text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-text-secondary">{row._id}</span>
                  <span className="text-white font-semibold">{formatMoney(row.total || 0)}</span>
                </div>
                <div className="h-1 bg-white/10 rounded">
                  <div
                    className="h-1 bg-cyber-yellow rounded"
                    style={{
                      width: `${Math.min(100, ((Number(row.total || 0) / Math.max(...(stats.trend || []).map((i) => Number(i.total || 0)), 1)) * 100))}%`,
                    }}
                  />
                </div>
              </div>
            ))}
            {!stats.trend?.length && <p className="text-sm text-text-muted">No trend data</p>}
          </div>
        </div>
      </div>

      {commissions.length === 0 ? (
        <div className="terminal-grid p-12 text-center text-text-muted">No commissions found.</div>
      ) : (
        <div className="space-y-3">
          {commissions.map((comm) => (
            <div key={comm.id} className="terminal-grid p-5" data-testid={`commission-item-${comm.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <DollarSign size={18} className="text-cyber-yellow" />
                    <span className="text-white font-semibold">{formatMoney(comm.amount, comm.currency)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${statusClass(comm.status)}`}>
                      {statusIcon(comm.status)} {labelize(comm.status)}
                    </span>
                    <span className="text-xs text-text-muted bg-white/5 px-2 py-0.5 rounded">{labelize(comm.type)}</span>
                    <span className="text-xs text-text-muted bg-white/5 px-2 py-0.5 rounded">{methodLabel(comm.payment_method)}</span>
                  </div>
                  <div className="text-xs text-text-muted font-mono flex items-center gap-3 flex-wrap">
                    <span>Tool: {comm.tool_name || comm.tool_id}</span>
                    {comm.affiliate_network && <span>Network: {comm.affiliate_network}</span>}
                    {comm.payment_reference && <span>Ref: {comm.payment_reference}</span>}
                    <span>Conversions: {comm.conversion_count || 1}</span>
                    {comm.commission_rate != null && <span>Rate: {comm.commission_rate}%</span>}
                    {comm.expected_payout_at && <span>Expected payout: {new Date(comm.expected_payout_at).toLocaleDateString()}</span>}
                    {comm.paid_at && <span>Paid at: {new Date(comm.paid_at).toLocaleDateString()}</span>}
                    <span>Created: {new Date(comm.created_at).toLocaleDateString()}</span>
                    {comm.notes && <span>Note: {comm.notes}</span>}
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap justify-end">
                  {comm.status === 'pending' && (
                    <button onClick={() => applyStatusMutation(comm.id, 'confirmed')} disabled={saving} className="btn-secondary text-xs">Confirm</button>
                  )}
                  {comm.status === 'confirmed' && (
                    <button onClick={() => applyStatusMutation(comm.id, 'paid')} disabled={saving} className="btn-secondary text-xs">Mark Paid</button>
                  )}
                  <button onClick={() => setEditingCommission(comm)} className="btn-secondary text-xs inline-flex items-center gap-1"><Pencil size={12} /> Edit</button>
                  <button onClick={() => handleDelete(comm.id)} className="btn-secondary text-xs inline-flex items-center gap-1 text-red-300"><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateCommissionModal
          tools={tools}
          onClose={() => setShowCreateModal(false)}
          onSave={async () => {
            setShowCreateModal(false);
            await loadData();
          }}
        />
      )}

      {editingCommission && (
        <CreateCommissionModal
          tools={tools}
          initialData={editingCommission}
          onClose={() => setEditingCommission(null)}
          onSave={async () => {
            setEditingCommission(null);
            await loadData();
          }}
        />
      )}
    </motion.div>
  );
};

const CreateCommissionModal = ({ tools, onClose, onSave, initialData = null }) => {
  const isEdit = !!initialData;
  const [formData, setFormData] = useState({
    tool_id: initialData?.tool_id || tools[0]?.id || '',
    type: initialData?.type || 'sale',
    amount: initialData?.amount ?? '',
    currency: initialData?.currency || 'USD',
    status: initialData?.status || 'pending',
    payment_method: initialData?.payment_method || 'bank_transfer',
    affiliate_network: initialData?.affiliate_network || '',
    payment_reference: initialData?.payment_reference || '',
    conversion_count: initialData?.conversion_count || 1,
    commission_rate: initialData?.commission_rate ?? '',
    period_start: toDateInputValue(initialData?.period_start),
    period_end: toDateInputValue(initialData?.period_end),
    expected_payout_at: toDateInputValue(initialData?.expected_payout_at),
    paid_at: toDateInputValue(initialData?.paid_at),
    notes: initialData?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        conversion_count: Number(formData.conversion_count || 1),
        commission_rate: formData.commission_rate === '' ? null : Number(formData.commission_rate),
        period_start: toIsoDate(formData.period_start),
        period_end: toIsoDate(formData.period_end),
        expected_payout_at: toIsoDate(formData.expected_payout_at),
        paid_at: toIsoDate(formData.paid_at),
      };

      if (isEdit) {
        await adminApi.updateCommission(initialData.id, payload);
      } else {
        await adminApi.createCommission(payload);
      }
      await onSave();
    } catch {
      alert(isEdit ? 'Failed to update commission' : 'Failed to create commission');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="create-commission-modal">
      <div className="terminal-grid p-8 max-w-3xl w-full max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>{isEdit ? 'Edit Commission' : 'Log Commission'}</h3>
          <button onClick={onClose} className="text-text-muted hover:text-white"><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-text-secondary text-sm mb-2">Tool</label>
              <select value={formData.tool_id} onChange={(e) => setFormData({ ...formData, tool_id: e.target.value })}
                className="w-full px-4 py-3 bg-surface border border-white/20 text-white" required data-testid="commission-tool-select">
                {tools.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Type</label>
              <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-4 py-3 bg-surface border border-white/20 text-white">
                {COMMISSION_TYPES.map((type) => <option key={type} value={type}>{labelize(type)}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Amount</label>
              <input type="number" step="0.01" value={formData.amount} required data-testid="commission-amount-input"
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Currency</label>
              <input type="text" value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value.toUpperCase() })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white" />
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Status</label>
              <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-3 bg-surface border border-white/20 text-white">
                {COMMISSION_STATUSES.map((status) => <option key={status} value={status}>{labelize(status)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Payment Method</label>
              <select value={formData.payment_method} onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                className="w-full px-4 py-3 bg-surface border border-white/20 text-white">
                {PAYMENT_METHODS.map((method) => <option key={method.id} value={method.id}>{method.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Affiliate Network</label>
              <input type="text" value={formData.affiliate_network}
                onChange={(e) => setFormData({ ...formData, affiliate_network: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white" placeholder="Impact, PartnerStack, etc" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Payment Reference</label>
              <input type="text" value={formData.payment_reference}
                onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white" placeholder="Txn/Invoice ID" />
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Conversion Count</label>
              <input type="number" min="1" value={formData.conversion_count}
                onChange={(e) => setFormData({ ...formData, conversion_count: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Commission Rate (%)</label>
              <input type="number" min="0" step="0.01" value={formData.commission_rate}
                onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white" />
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Period Start</label>
              <input type="date" value={formData.period_start}
                onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Period End</label>
              <input type="date" value={formData.period_end}
                onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white" />
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Expected Payout Date</label>
              <input type="date" value={formData.expected_payout_at}
                onChange={(e) => setFormData({ ...formData, expected_payout_at: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white" />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Paid Date</label>
              <input type="date" value={formData.paid_at}
                onChange={(e) => setFormData({ ...formData, paid_at: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white" />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-text-secondary text-sm mb-2">Notes</label>
            <input type="text" value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-3 bg-transparent border border-white/20 text-white" placeholder="Optional notes..." />
          </div>

          <div className="flex gap-4 mt-6">
            <button type="submit" disabled={saving} className="btn-primary" data-testid="save-commission-button">
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Save Commission'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommissionsManager;
